import clsx from 'clsx';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, TextSkeleton, Tooltip } from '@gitbook/spine-react';

/**
 * Displays the agent's question with answer options shown as cards at the end of the conversation.
 * User can either pick one or answer in their own words in the composer.
 */
export function AgentChatQuestion(props: {
    /** The question, when the conversation does not already ask it. */
    question?: string;
    options: api.AgentQuestionOption[];
    /** Submit a picked option's label. */
    onSubmit: (answer: string) => void;
    /** Disable the options, e.g. while the agent is not idle. */
    disabled?: boolean;
    /** Disable the options and say why on hover, e.g. once no message can be sent. */
    disabledReason?: string;
    /** Show the options as skeletons that can't be picked yet, e.g. while what they do loads. */
    loading?: boolean;
}) {
    const {
        question,
        options,
        onSubmit,
        disabled = false,
        disabledReason,
        loading = false,
    } = props;

    return (
        <div className="flex flex-col gap-2">
            {question ? <p className="text-ui-base text-neutral-high">{question}</p> : null}
            {options.map((option) => {
                const card = (
                    <AtomButton
                        key={option.label}
                        disabled={disabled || loading || Boolean(disabledReason)}
                        onPress={() => onSubmit(option.label)}
                        className={clsx(
                            'flex items-center gap-3 rounded-lg border bg-base px-3 py-2.5 text-left transition-colors',
                            'hover:bg-standout-side',
                            'focus-visible:focus-ring focus-visible:outline-hidden',
                            'disabled:pointer-events-none',
                            !loading && 'disabled:opacity-60'
                        )}
                    >
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <OptionText
                                loading={loading}
                                className="text-ui-base text-neutral-high"
                            >
                                {option.label}
                            </OptionText>
                            {option.description ? (
                                <OptionText loading={loading} className="text-ui-small text-muted">
                                    {option.description}
                                </OptionText>
                            ) : null}
                        </span>
                        <Icon.ChevronRight className="size-4 shrink-0 text-muted" />
                    </AtomButton>
                );
                // A disabled button gets no hover, so the reason hangs on a wrapper around it.
                return disabledReason ? (
                    <Tooltip key={option.label} label={disabledReason}>
                        <span className="flex flex-col">{card}</span>
                    </Tooltip>
                ) : (
                    card
                );
            })}
        </div>
    );
}

/** An option's text, or a skeleton of the same size while it loads. */
function OptionText(props: { loading: boolean; className: string; children: React.ReactNode }) {
    const { loading, className, children } = props;
    return loading ? (
        <TextSkeleton className={className}>{children}</TextSkeleton>
    ) : (
        <span className={className}>{children}</span>
    );
}
